// Phase 4.1 — Billing screen, migrated to shadcn/Tailwind.
//
// UI only: the MUI Table, the ApparelSubmenu toolbar and the antd Dropdown menu
// are replaced by the shared DataTable (BillingTable) + a PageHeader with
// built-in search/pagination; the MUI Drawer invoice view becomes a Sheet
// (ViewBillingSheet). Every dispatch / useSelector / navigate / payload below is
// the legacy one — the @react-pdf receipt (handlePrint) is untouched.
import { useDispatch, useSelector } from "react-redux";
import { clearProductItemListAction, getBillingListAction, getBillNavigationRowAction, getInvoiceDetailsAction, getReferenceDataAction } from "../Store/actions/apparelAction";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { deepCopyFunction } from "../Utils/helpers";
import { useLocation, useNavigate } from "react-router-dom";
import BillReceipt from "./billReceipt";
import { pdf } from "@react-pdf/renderer";
import ApparelMenu from "./SubComponents/apparelMenu";
import AppConstant from "../Globals/AppConstants";
import { Button } from "ui/button";
import BillingTable from "./orders/BillingTable";
import ViewBillingSheet from "./orders/ViewBillingSheet";
import useBillNavigation from "./orders/useBillNavigation";

// referenceGroupId 4 = Payment Method (see apparelReducer.ts's
// API_GET_REFERENCE_DATA_SUCCESS split: group 4 -> paymentTypeData).
const PAYMENT_METHOD_REFERENCE_GROUP_ID = 4;

const Billing = () => {
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { billingList, getInvoiceDetailsOnload, getInvoiceDetailsData, billingListOnload, paymentTypeData, billNavigationRow, billNavigationRowOnload } = apparelReducerState;
    const paymentOptions = paymentTypeData?.getReferenceData;
    const billing = billingList?.getBillingList?.billingDetails;
    const billingPage = billingList?.getBillingList?.page;
    const dispatch = useDispatch();
    const [page] = useState(1);
    const [openViewBilling, setOpenViewBilling] = useState(false);
    const getInvoiceDetails = getInvoiceDetailsData?.getInvoiceDetails;
    const [getInvoiceDetailsload, setGetInvoiceDetailsload] = useState(false);
    const [callGetInvoice, setCallGetInvoice] = useState(false);
    const [getCustomerDetails, setGetCustomerDetails] = useState<any>();
    const [iscycleCountCompleted, setIscycleCountCompleted] = useState<any>();
    const navigate = useNavigate();
    const location = useLocation();
    // Arriving from Audit Log: re-run this screen's own search with the
    // record's invoice id, then open its edit page once the matching row
    // (by billingId, the uniqueKey alias) shows up in the list.
    const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);

    const handlePagination = (page: any, offset: any) => {
        setFilterData({ ...filterData, offset: offset, page: page })
    }

    useEffect(() => {
        setIscycleCountCompleted(billingList?.getBillingList?.isCycleCountcompleted)
    }, [billingList])

    const searchtemp: any = {
        searchText: "",
        paymentFilter: "",
        offset: 0,
        limit: 10,
        page: 1
    };
    const [filterData, setFilterData] = useState<any>(() => {
        const auditCode = location.state?.auditSearchCode;
        return auditCode ? { ...deepCopyFunction(searchtemp), searchText: auditCode } : deepCopyFunction(searchtemp);
    });

    const getsearchTextdataList = (searchText: any) => {
        setFilterData({ ...filterData, searchText: searchText, offset: 0, page: 1 })
    }

    // "" = All; any other value is a Payment Method reference name, matched by
    // name server-side (see BillingService.billingList's paymentFilter handling).
    const onPaymentFilterChange = (paymentFilter: string) => {
        setFilterData({ ...filterData, paymentFilter: paymentFilter === "ALL" ? "" : paymentFilter, offset: 0, page: 1 })
    }

    // DataTable gives a 1-based page; translate to the legacy offset math.
    const onPageChange = (nextPage: number) => {
        let offset = ((nextPage * 10) - 10 === 0) ? 0 : (nextPage * 10) - 10;
        handlePagination(page, offset);
    }

    const billingListApi = () => {
        let payload: any = {
            searchText: filterData?.searchText,
            paymentFilter: filterData?.paymentFilter,
            limit: 10,
            offset: filterData?.offset
        }
        dispatch(getBillingListAction(payload))
    }

    useEffect(() => {
        if (filterData) {
            billingListApi()
        }
    }, [filterData])

    // Payment Method reference rows (referenceGroupId 4) -> paymentTypeData,
    // used to populate the payment filter dropdown.
    useEffect(() => {
        dispatch(getReferenceDataAction({ referenceGroupId: PAYMENT_METHOD_REFERENCE_GROUP_ID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getInvoiceDetailsApi = (item: any) => {
        let payload = {
            invoiceId: item?.invoiceId
        }
        dispatch(getInvoiceDetailsAction(payload))
        setGetInvoiceDetailsload(true);
        setOpenViewBilling(true)
        // Use the clicked row directly instead of re-finding it in `billing` —
        // BillingTable memoizes its row action handlers once (columns useMemo
        // has an empty dep array), so this function can still be invoked via a
        // stale closure whose `billing` snapshot no longer contains (or never
        // contained) the current row, silently breaking Prev/Next in
        // useBillNavigation (which needs a matching currentInvoiceId). `item`
        // itself is always the fresh row the user just clicked.
        setGetCustomerDetails(item)
    }

    useEffect(() => {
        if (getInvoiceDetailsload == true && getInvoiceDetailsOnload == false) {
            setOpenViewBilling(true)
            setGetInvoiceDetailsload(false);
            setCallGetInvoice(true)
        }
    }, [getInvoiceDetailsload, getInvoiceDetailsOnload])

    const closeViewBilling = () => {
        setCallGetInvoice(false)
        setOpenViewBilling(false)
    }

    const handleEdit = (item: any) => {
        // listFilter lets the Edit Bill screen re-query the SAME sequence this
        // screen is showing when its prev/next steps past the loaded page.
        navigate("/create/bill", {
            state: {
                data: item,
                isEdit: 1,
                listFilter: { searchText: filterData?.searchText, paymentFilter: filterData?.paymentFilter }
            }
        })
    }

    // Prev/Next inside the View Billing sheet — steps by invoice NUMBER
    // (independent of this screen's own search/payment filter), probing one
    // invoiceId at a time via its own reducer slice (billNavigationRow) so it
    // never disturbs the Customer Bills table's own list state.
    const billNavigation = useBillNavigation({
        currentInvoiceId: getCustomerDetails?.invoiceId,
        probeResult: billNavigationRow,
        probeLoading: billNavigationRowOnload,
        onProbe: (invoiceId: string) => dispatch(getBillNavigationRowAction({ searchText: invoiceId, paymentFilter: "", limit: 1, offset: 0 })),
        onSelect: getInvoiceDetailsApi,
        onExhausted: (direction) => toast.info(direction === "prev" ? "No earlier bill found nearby." : "No later bill found nearby."),
    })

    // Same hand-off as the list row's Edit action; the sheet only has the row it
    // was opened with (getCustomerDetails), which is the very object the row
    // menu would have passed.
    const handleEditFromView = () => {
        if (!getCustomerDetails) return;
        closeViewBilling();
        handleEdit(getCustomerDetails);
    }

    useEffect(() => {
        if (!pendingAuditRecordId || !billing?.length) return;
        const match = billing.find((row: any) => row?.billingId === pendingAuditRecordId);
        if (match) {
            handleEdit(match);
            setPendingAuditRecordId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAuditRecordId, billing])

    // Legacy ApparelSubmenu.setOpenDrawer() for this screen (printSticker=false):
    // warn if a cycle count is pending, else clear the product-item list and go
    // to Create Bill.
    const handleAddBill = () => {
        if (iscycleCountCompleted > 0) {
            toast.warning(AppConstant?.PLEASE_COMPLETE_THE_PENDING_CYCLECOUNT)
        } else {
            dispatch(clearProductItemListAction());
            navigate("/create/bill")
        }
    }

    const handlePrint = async () => {
        //Converting React Component to PDF
        try {
            // Generate PDF blob
            const blob = await pdf(<BillReceipt invoiceData={getInvoiceDetails} customerDetails={getCustomerDetails} />).toBlob();

            // Create object URL
            const pdfUrl = URL.createObjectURL(blob);

            // Create iframe for printing
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
                setTimeout(() => {
                    iframe.contentWindow?.print();
                }, 500);
            };
        } catch (error) {
            console.error('Error generating PDF:', error);
            // Show error message to user
            toast.error('Failed to generate receipt. Please try again.');
        }
    };

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="2" />
            <BillingTable
                data={billing}
                loading={billingListOnload}
                onView={getInvoiceDetailsApi}
                onEdit={handleEdit}
                searchValue={filterData?.searchText}
                onSearch={getsearchTextdataList}
                searchPlaceholder={"Search By Invoice ID/Customer Name/Product ID"}
                paymentFilter={filterData?.paymentFilter}
                onPaymentFilterChange={onPaymentFilterChange}
                paymentOptions={paymentOptions}
                title="Customer Bills"
                headerActions={<Button onClick={handleAddBill}>{AppConstant?.add}</Button>}
                pagination={{
                    page: billingPage?.currentPage || 1,
                    total: billingPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <ViewBillingSheet
                open={openViewBilling}
                onClose={closeViewBilling}
                customerDetails={getCustomerDetails}
                invoiceDetails={getInvoiceDetails}
                callGetInvoice={callGetInvoice}
                onPrint={handlePrint}
                navigation={billNavigation}
                onEdit={handleEditFromView}
            />
        </div>
    )
}

export default Billing;
